import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, BarChart3, ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, User, Trophy, Clock, Home, Calendar, MapPin } from "lucide-react";
import { format, startOfDay, endOfDay, startOfMonth, endOfMonth, startOfYear, endOfYear, subDays, isToday, isYesterday, differenceInDays, getWeek, startOfWeek, endOfWeek, eachMonthOfInterval, eachWeekOfInterval, eachDayOfInterval } from "date-fns";
import { de } from "date-fns/locale";

interface TeamMemberStatisticsProps {
  teamId: string;
  organizationId: string;
}

interface DailyTagCount {
  win: number;
  hot: number;
  kt: number;
  bk: number;
  na: number;
  ki: number;
  no: number;
}

interface DailyActivity {
  date: string;
  count: number;
  tags: DailyTagCount;
}

interface HouseDetail {
  houseId: string;
  houseNumber: string;
  tags: DailyTagCount;
  unitCount: number;
}

interface StreetDetail {
  streetId: string;
  streetName: string;
  tags: DailyTagCount;
  count: number;
  houses: HouseDetail[];
}

interface AreaDetail {
  areaId: string;
  areaName: string;
  streets: StreetDetail[];
}

interface WeekGroup {
  weekNumber: number;
  weekStart: Date;
  weekEnd: Date;
  days: DailyActivity[];
  totalCount: number;
  tags: DailyTagCount;
}

interface MonthGroup {
  month: number;
  monthName: string;
  weeks: WeekGroup[];
  totalCount: number;
  tags: DailyTagCount;
}

interface MemberStats {
  userId: string;
  firstName: string;
  lastName: string;
  totalUnits: number;
  winCount: number;
  kiCount: number;
  naCount: number;
  hotCount: number;
  bkCount: number;
  ktCount: number;
  noCount: number;
  dailyActivity: DailyActivity[];
  monthlyGroups: MonthGroup[];
  lastActivityAt: string | null;
}

type TimeRange = 'day' | 'week' | 'month' | 'year';

export function TeamMemberStatistics({ teamId, organizationId }: TeamMemberStatisticsProps) {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [memberStats, setMemberStats] = useState<MemberStats[]>([]);
  const [expandedMember, setExpandedMember] = useState<string | null>(null);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);
  const [expandedWeek, setExpandedWeek] = useState<string | null>(null);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [dayDetails, setDayDetails] = useState<{ [key: string]: AreaDetail[] }>({});
  const [loadingDay, setLoadingDay] = useState<string | null>(null);
  const [expandedTag, setExpandedTag] = useState<string | null>(null);
  const [tagDetails, setTagDetails] = useState<{ [key: string]: AreaDetail[] }>({});
  const [loadingTag, setLoadingTag] = useState<string | null>(null);
  const [activeTagFilter, setActiveTagFilter] = useState<{ [userId: string]: string | null }>({});

  useEffect(() => {
    fetchStatistics();
  }, [teamId, timeRange]);

  const getDateRange = () => {
    const now = new Date();
    switch (timeRange) {
      case 'day':
        return { start: startOfDay(now), end: endOfDay(now) };
      case 'week':
        return { start: startOfDay(subDays(now, 7)), end: endOfDay(now) };
      case 'month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'year':
        return { start: startOfYear(now), end: endOfYear(now) };
    }
  };

  const countTags = (tags: string[] | null): DailyTagCount => {
    const t = tags || [];
    return {
      win: t.includes('WIN') ? 1 : 0,
      hot: t.includes('HOT') ? 1 : 0,
      kt: t.includes('KT') ? 1 : 0,
      bk: t.includes('BK') ? 1 : 0,
      na: t.includes('NA') ? 1 : 0,
      ki: t.includes('KI') ? 1 : 0,
      no: t.includes('NO') ? 1 : 0,
    };
  };

  const addTags = (a: DailyTagCount, b: DailyTagCount): DailyTagCount => ({
    win: a.win + b.win,
    hot: a.hot + b.hot,
    kt: a.kt + b.kt,
    bk: a.bk + b.bk,
    na: a.na + b.na,
    ki: a.ki + b.ki,
    no: a.no + b.no,
  });

  const emptyTags = (): DailyTagCount => ({ win: 0, hot: 0, kt: 0, bk: 0, na: 0, ki: 0, no: 0 });

  // Filter daily activity by active tag filter
  const filterDailyActivityByTag = (dailyActivity: DailyActivity[], tagFilter: string | null): DailyActivity[] => {
    if (!tagFilter) return dailyActivity;
    
    return dailyActivity
      .map(day => {
        const tagKey = tagFilter.toLowerCase() as keyof DailyTagCount;
        const filteredCount = day.tags[tagKey];
        if (filteredCount === 0) return null;
        
        return {
          ...day,
          count: filteredCount,
          tags: {
            ...emptyTags(),
            [tagKey]: filteredCount
          }
        };
      })
      .filter((day): day is DailyActivity => day !== null);
  };

  // Filter month groups by active tag filter
  const filterMonthGroupsByTag = (monthGroups: MonthGroup[], tagFilter: string | null): MonthGroup[] => {
    if (!tagFilter) return monthGroups;
    
    const tagKey = tagFilter.toLowerCase() as keyof DailyTagCount;
    
    return monthGroups
      .map(month => {
        const filteredWeeks = month.weeks
          .map(week => {
            const filteredDays = week.days
              .map(day => {
                const filteredCount = day.tags[tagKey];
                if (filteredCount === 0) return null;
                return {
                  ...day,
                  count: filteredCount,
                  tags: { ...emptyTags(), [tagKey]: filteredCount }
                };
              })
              .filter((day): day is DailyActivity => day !== null);
            
            if (filteredDays.length === 0) return null;
            
            const weekTotal = filteredDays.reduce((sum, d) => sum + d.count, 0);
            return {
              ...week,
              days: filteredDays,
              totalCount: weekTotal,
              tags: { ...emptyTags(), [tagKey]: weekTotal }
            };
          })
          .filter((week): week is WeekGroup => week !== null);
        
        if (filteredWeeks.length === 0) return null;
        
        const monthTotal = filteredWeeks.reduce((sum, w) => sum + w.totalCount, 0);
        return {
          ...month,
          weeks: filteredWeeks,
          totalCount: monthTotal,
          tags: { ...emptyTags(), [tagKey]: monthTotal }
        };
      })
      .filter((month): month is MonthGroup => month !== null);
  };

  const getManualActivityDate = (unit: { tags: string[] | null; created_at: string; last_contact_at: string | null }): Date => {
    const tags = unit.tags || [];
    const hasStrongManualTags = tags.some(t => ['WIN', 'KT', 'KI', 'NO'].includes(t));
    
    const createdAt = new Date(unit.created_at);
    const lastContactAt = unit.last_contact_at ? new Date(unit.last_contact_at) : null;
    
    const isReasonableHour = (date: Date) => {
      const hour = date.getHours();
      return hour >= 6 && hour <= 22;
    };
    
    if (hasStrongManualTags && lastContactAt && isReasonableHour(lastContactAt)) {
      return lastContactAt;
    }
    
    return createdAt;
  };

  // Group daily activities into months → weeks → days hierarchy
  const groupActivitiesHierarchically = (dailyActivity: DailyActivity[], dateRange: { start: Date; end: Date }): MonthGroup[] => {
    const activityMap = new Map<string, DailyActivity>();
    dailyActivity.forEach(day => {
      activityMap.set(day.date, day);
    });

    const months = eachMonthOfInterval({ start: dateRange.start, end: dateRange.end });
    
    const monthGroups: MonthGroup[] = months.map(monthDate => {
      const monthStart = startOfMonth(monthDate);
      const monthEnd = endOfMonth(monthDate);
      
      // Get weeks in this month
      const weeksInMonth = eachWeekOfInterval(
        { start: monthStart, end: monthEnd },
        { weekStartsOn: 1 } // Monday start
      );
      
      const weekGroups: WeekGroup[] = weeksInMonth.map(weekStart => {
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
        const weekNumber = getWeek(weekStart, { weekStartsOn: 1, firstWeekContainsDate: 4 });
        
        // Get days in this week (but only within our date range and month)
        const daysInWeek = eachDayOfInterval({ 
          start: weekStart < monthStart ? monthStart : weekStart, 
          end: weekEnd > monthEnd ? monthEnd : weekEnd 
        });
        
        const days: DailyActivity[] = daysInWeek
          .filter(d => d >= dateRange.start && d <= dateRange.end)
          .map(d => {
            const dateStr = format(d, 'yyyy-MM-dd');
            return activityMap.get(dateStr) || { date: dateStr, count: 0, tags: emptyTags() };
          })
          .filter(d => d.count > 0); // Only include days with activity
        
        const weekTags = days.reduce((acc, d) => addTags(acc, d.tags), emptyTags());
        const weekTotal = days.reduce((acc, d) => acc + d.count, 0);
        
        return {
          weekNumber,
          weekStart,
          weekEnd,
          days,
          totalCount: weekTotal,
          tags: weekTags
        };
      }).filter(w => w.totalCount > 0); // Only include weeks with activity
      
      const monthTags = weekGroups.reduce((acc, w) => addTags(acc, w.tags), emptyTags());
      const monthTotal = weekGroups.reduce((acc, w) => acc + w.totalCount, 0);
      
      return {
        month: monthDate.getMonth(),
        monthName: format(monthDate, 'MMMM yyyy', { locale: de }),
        weeks: weekGroups,
        totalCount: monthTotal,
        tags: monthTags
      };
    }).filter(m => m.totalCount > 0); // Only include months with activity
    
    return monthGroups.reverse(); // Most recent first
  };

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const { data: members, error: membersError } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name')
        .eq('team_id', teamId);

      if (membersError) throw membersError;

      const { start, end } = getDateRange();

      const statsPromises = (members || []).map(async (member) => {
        const { data: allUnits, error: unitsError } = await supabase
          .from('d2d_units')
          .select('id, tags, updated_at, created_at, last_contact_at')
          .eq('user_id', member.user_id)
          .eq('organization_id', organizationId);

        if (unitsError) {
          console.error('Error fetching units for member:', unitsError);
          return null;
        }

        const unitList = (allUnits || []).filter(unit => {
          const activityDate = getManualActivityDate(unit);
          return activityDate >= start && activityDate <= end;
        });

        let winCount = 0, kiCount = 0, naCount = 0, hotCount = 0, bkCount = 0, ktCount = 0, noCount = 0;
        let lastActivityAt: string | null = null;
        
        unitList.forEach(unit => {
          const tags = unit.tags || [];
          if (tags.includes('WIN')) winCount++;
          if (tags.includes('KI')) kiCount++;
          if (tags.includes('NA')) naCount++;
          if (tags.includes('HOT')) hotCount++;
          if (tags.includes('BK')) bkCount++;
          if (tags.includes('KT')) ktCount++;
          if (tags.includes('NO')) noCount++;
          
          const activityDate = getManualActivityDate(unit);
          const activityDateStr = activityDate.toISOString();
          
          if (!lastActivityAt || activityDateStr > lastActivityAt) {
            lastActivityAt = activityDateStr;
          }
        });

        // Calculate daily activity with tag counts
        const dailyMap = new Map<string, { count: number; tags: DailyTagCount }>();
        unitList.forEach(unit => {
          const activityDate = getManualActivityDate(unit);
          const date = format(activityDate, 'yyyy-MM-dd');
          const existing = dailyMap.get(date) || { count: 0, tags: emptyTags() };
          const unitTags = countTags(unit.tags);
          dailyMap.set(date, {
            count: existing.count + 1,
            tags: addTags(existing.tags, unitTags)
          });
        });

        const dailyActivity: DailyActivity[] = Array.from(dailyMap.entries())
          .map(([date, data]) => ({ date, count: data.count, tags: data.tags }))
          .sort((a, b) => b.date.localeCompare(a.date));

        // Group hierarchically for year/month views
        const monthlyGroups = groupActivitiesHierarchically(dailyActivity, { start, end });

        return {
          userId: member.user_id,
          firstName: member.first_name || '',
          lastName: member.last_name || '',
          totalUnits: unitList.length,
          winCount,
          kiCount,
          naCount,
          hotCount,
          bkCount,
          ktCount,
          noCount,
          dailyActivity,
          monthlyGroups,
          lastActivityAt
        } as MemberStats;
      });

      const stats = (await Promise.all(statsPromises)).filter(Boolean) as MemberStats[];
      stats.sort((a, b) => b.totalUnits - a.totalUnits);
      setMemberStats(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDayDetails = async (userId: string, date: string) => {
    const key = `${userId}-${date}`;
    if (dayDetails[key]) {
      setExpandedDay(expandedDay === key ? null : key);
      return;
    }

    setLoadingDay(key);
    try {
      const dayStart = startOfDay(new Date(date));
      const dayEnd = endOfDay(new Date(date));

      const { data: allUnits, error: unitsError } = await supabase
        .from('d2d_units')
        .select('id, tags, house_id, created_at, last_contact_at')
        .eq('user_id', userId)
        .eq('organization_id', organizationId);

      if (unitsError) throw unitsError;

      const units = (allUnits || []).filter(unit => {
        const activityDate = getManualActivityDate(unit);
        return activityDate >= dayStart && activityDate <= dayEnd;
      });

      if (units.length === 0) {
        setDayDetails(prev => ({ ...prev, [key]: [] }));
        setExpandedDay(key);
        setLoadingDay(null);
        return;
      }

      const houseIds = [...new Set(units.map(u => u.house_id))];

      const { data: houses, error: housesError } = await supabase
        .from('d2d_houses')
        .select('id, street_id, house_number')
        .in('id', houseIds);

      if (housesError) throw housesError;

      const streetIds = [...new Set((houses || []).map(h => h.street_id))];

      const { data: streets, error: streetsError } = await supabase
        .from('d2d_streets')
        .select('id, name, area_id')
        .in('id', streetIds);

      if (streetsError) throw streetsError;

      const areaIds = [...new Set((streets || []).map(s => s.area_id))];

      const { data: areas, error: areasError } = await supabase
        .from('d2d_areas')
        .select('id, name')
        .in('id', areaIds);

      if (areasError) throw areasError;

      const houseMap = new Map((houses || []).map(h => [h.id, h]));
      const streetMap = new Map((streets || []).map(s => [s.id, s]));
      const areaMap = new Map((areas || []).map(a => [a.id, a]));

      const resultMap = new Map<string, { 
        name: string; 
        streets: Map<string, { 
          name: string; 
          tags: DailyTagCount; 
          count: number;
          houses: Map<string, { houseNumber: string; tags: DailyTagCount; unitCount: number }>;
        }> 
      }>();

      units.forEach((unit) => {
        const house = houseMap.get(unit.house_id);
        if (!house) return;

        const street = streetMap.get(house.street_id);
        if (!street) return;

        const area = areaMap.get(street.area_id);
        if (!area) return;

        if (!resultMap.has(area.id)) {
          resultMap.set(area.id, { name: area.name, streets: new Map() });
        }

        const areaData = resultMap.get(area.id)!;
        if (!areaData.streets.has(street.id)) {
          areaData.streets.set(street.id, { name: street.name, tags: emptyTags(), count: 0, houses: new Map() });
        }

        const streetData = areaData.streets.get(street.id)!;
        streetData.count++;
        streetData.tags = addTags(streetData.tags, countTags(unit.tags));

        if (!streetData.houses.has(house.id)) {
          streetData.houses.set(house.id, { houseNumber: house.house_number, tags: emptyTags(), unitCount: 0 });
        }
        const houseData = streetData.houses.get(house.id)!;
        houseData.unitCount++;
        houseData.tags = addTags(houseData.tags, countTags(unit.tags));
      });

      const details: AreaDetail[] = Array.from(resultMap.entries()).map(([areaId, areaData]) => ({
        areaId,
        areaName: areaData.name,
        streets: Array.from(areaData.streets.entries()).map(([streetId, streetData]) => ({
          streetId,
          streetName: streetData.name,
          tags: streetData.tags,
          count: streetData.count,
          houses: Array.from(streetData.houses.entries()).map(([houseId, houseData]) => ({
            houseId,
            houseNumber: houseData.houseNumber,
            tags: houseData.tags,
            unitCount: houseData.unitCount
          })).sort((a, b) => a.houseNumber.localeCompare(b.houseNumber, undefined, { numeric: true }))
        }))
      }));

      setDayDetails(prev => ({ ...prev, [key]: details }));
      setExpandedDay(key);
    } catch (error) {
      console.error('Error fetching day details:', error);
    } finally {
      setLoadingDay(null);
    }
  };

  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'day': return 'Heute';
      case 'week': return 'Letzte 7 Tage';
      case 'month': return format(new Date(), 'MMMM yyyy', { locale: de });
      case 'year': return format(new Date(), 'yyyy');
    }
  };

  const formatLastActivity = (dateString: string | null): string => {
    if (!dateString) return 'Keine Aktivität';
    
    const date = new Date(dateString);
    const timeStr = format(date, 'HH:mm', { locale: de });
    
    if (isToday(date)) {
      return `Heute um ${timeStr} Uhr`;
    }
    
    if (isYesterday(date)) {
      return `Gestern um ${timeStr} Uhr`;
    }
    
    const daysDiff = differenceInDays(new Date(), date);
    const dateStr = format(date, 'dd.MM.yyyy', { locale: de });
    
    return `Vor ${daysDiff} Tagen am ${dateStr} um ${timeStr} Uhr`;
  };

  const fetchTagDetails = async (userId: string, tag: string) => {
    const key = `${userId}-${tag}`;
    if (tagDetails[key]) {
      setExpandedTag(expandedTag === key ? null : key);
      return;
    }

    setLoadingTag(key);
    try {
      const { start, end } = getDateRange();

      const { data: allUnits, error: unitsError } = await supabase
        .from('d2d_units')
        .select('id, tags, house_id, created_at, last_contact_at')
        .eq('user_id', userId)
        .eq('organization_id', organizationId)
        .contains('tags', [tag]);

      if (unitsError) throw unitsError;

      const units = (allUnits || []).filter(unit => {
        const activityDate = getManualActivityDate(unit);
        return activityDate >= start && activityDate <= end;
      });

      if (units.length === 0) {
        setTagDetails(prev => ({ ...prev, [key]: [] }));
        setExpandedTag(key);
        setLoadingTag(null);
        return;
      }

      const houseIds = [...new Set(units.map(u => u.house_id))];

      const { data: houses, error: housesError } = await supabase
        .from('d2d_houses')
        .select('id, street_id, house_number')
        .in('id', houseIds);

      if (housesError) throw housesError;

      const streetIds = [...new Set((houses || []).map(h => h.street_id))];

      const { data: streets, error: streetsError } = await supabase
        .from('d2d_streets')
        .select('id, name, area_id')
        .in('id', streetIds);

      if (streetsError) throw streetsError;

      const areaIds = [...new Set((streets || []).map(s => s.area_id))];

      const { data: areas, error: areasError } = await supabase
        .from('d2d_areas')
        .select('id, name')
        .in('id', areaIds);

      if (areasError) throw areasError;

      const houseMap = new Map((houses || []).map(h => [h.id, h]));
      const streetMap = new Map((streets || []).map(s => [s.id, s]));
      const areaMap = new Map((areas || []).map(a => [a.id, a]));

      const resultMap = new Map<string, { 
        name: string; 
        streets: Map<string, { 
          name: string; 
          tags: DailyTagCount; 
          count: number;
          houses: Map<string, { houseNumber: string; tags: DailyTagCount; unitCount: number }>;
        }> 
      }>();

      units.forEach((unit) => {
        const house = houseMap.get(unit.house_id);
        if (!house) return;

        const street = streetMap.get(house.street_id);
        if (!street) return;

        const area = areaMap.get(street.area_id);
        if (!area) return;

        if (!resultMap.has(area.id)) {
          resultMap.set(area.id, { name: area.name, streets: new Map() });
        }

        const areaData = resultMap.get(area.id)!;
        if (!areaData.streets.has(street.id)) {
          areaData.streets.set(street.id, { name: street.name, tags: emptyTags(), count: 0, houses: new Map() });
        }

        const streetData = areaData.streets.get(street.id)!;
        streetData.count++;
        streetData.tags = addTags(streetData.tags, countTags(unit.tags));

        if (!streetData.houses.has(house.id)) {
          streetData.houses.set(house.id, { houseNumber: house.house_number, tags: emptyTags(), unitCount: 0 });
        }
        const houseData = streetData.houses.get(house.id)!;
        houseData.unitCount++;
        houseData.tags = addTags(houseData.tags, countTags(unit.tags));
      });

      const details: AreaDetail[] = Array.from(resultMap.entries()).map(([areaId, areaData]) => ({
        areaId,
        areaName: areaData.name,
        streets: Array.from(areaData.streets.entries()).map(([streetId, streetData]) => ({
          streetId,
          streetName: streetData.name,
          tags: streetData.tags,
          count: streetData.count,
          houses: Array.from(streetData.houses.entries()).map(([houseId, houseData]) => ({
            houseId,
            houseNumber: houseData.houseNumber,
            tags: houseData.tags,
            unitCount: houseData.unitCount
          })).sort((a, b) => a.houseNumber.localeCompare(b.houseNumber, undefined, { numeric: true }))
        }))
      }));

      setTagDetails(prev => ({ ...prev, [key]: details }));
      setExpandedTag(key);
    } catch (error) {
      console.error('Error fetching tag details:', error);
    } finally {
      setLoadingTag(null);
    }
  };

  const ClickableTagBadge = ({ label, count, color, userId }: { label: string; count: number; color: string; userId: string }) => {
    const tagKey = `${userId}-${label}`;
    const isExpanded = expandedTag === tagKey;
    const isLoading = loadingTag === tagKey;
    const details = tagDetails[tagKey];
    
    // State für Gebiete und Straßen innerhalb dieses Tags
    // 0 = alles offen, 1 = nur Gebiete offen, 2 = alles zu
    const [collapseLevel, setCollapseLevel] = useState<0 | 1 | 2>(0);
    
    // Ableiten der expanded states aus collapseLevel
    const getAreaExpanded = () => collapseLevel < 2;
    const getStreetExpanded = () => collapseLevel === 0;
    
    // 3-Stufen Toggle: Alles offen → Nur Gebiete → Alles zu → Alles offen
    const toggleCollapseLevel = (e: React.MouseEvent) => {
      e.stopPropagation();
      setCollapseLevel(prev => ((prev + 1) % 3) as 0 | 1 | 2);
    };
    
    // Icon basierend auf Level
    const getCollapseIcon = () => {
      if (collapseLevel === 0) return <ChevronsUpDown className="h-2.5 w-2.5" />; // Alles offen → wird zu Gebiete
      if (collapseLevel === 1) return <ChevronUp className="h-2.5 w-2.5" />; // Nur Gebiete → wird zu alles zu
      return <ChevronDown className="h-2.5 w-2.5" />; // Alles zu → wird zu alles offen
    };

    if (count === 0) return null;

    return (
      <div>
        <div className={`flex items-center gap-1 ${isExpanded ? `${color} rounded px-1.5 py-0.5` : ''}`}>
          <div 
            className={`flex items-center gap-1 ${isExpanded ? '' : `px-1.5 py-0.5 rounded ${color}`} text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity`}
            onClick={(e) => {
              e.stopPropagation();
              fetchTagDetails(userId, label);
            }}
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            <span>{label}</span>
            <span className="font-bold">{count}</span>
          </div>
          {/* 3-Stufen Collapse Button - immer sichtbar wenn expanded */}
          {isExpanded && (
            <button
              onClick={toggleCollapseLevel}
              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium cursor-pointer hover:opacity-80 transition-opacity bg-background border border-border"
              title={collapseLevel === 0 ? "Straßen zuklappen" : collapseLevel === 1 ? "Alles zuklappen" : "Alles aufklappen"}
            >
              {getCollapseIcon()}
            </button>
          )}
        </div>
        
        {isExpanded && details && (
          <div className="mt-2 ml-2 pl-3 border-l-2 border-muted space-y-2">
            {details.map((area) => {
              // Calculate tag count for this area
              const areaTagCount = area.streets.reduce((sum, street) => sum + street.count, 0);
              
              return (
                <Collapsible 
                  key={area.areaId} 
                  open={getAreaExpanded()}
                >
                  <div className="space-y-1">
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:text-primary">
                      {getAreaExpanded() ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRight className="h-3 w-3" />
                      )}
                      <MapPin className="h-3 w-3 text-primary" />
                      <span>{area.areaName}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${color}`}>
                        {areaTagCount}
                      </span>
                    </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="ml-5 space-y-2">
                      {area.streets.map((street) => {
                        return (
                          <Collapsible 
                            key={street.streetId}
                            open={getStreetExpanded()}
                          >
                            <div className="space-y-1">
                              <CollapsibleTrigger className="flex items-center justify-between w-full text-xs py-1 px-2 rounded bg-muted/30 font-medium hover:bg-muted/50">
                                <div className="flex items-center gap-1">
                                  {getStreetExpanded() ? (
                                    <ChevronDown className="h-3 w-3" />
                                  ) : (
                                    <ChevronRight className="h-3 w-3" />
                                  )}
                                  <span>{street.streetName}</span>
                                </div>
                                <span className="text-muted-foreground">({street.count})</span>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="ml-4 grid grid-cols-2 sm:grid-cols-3 gap-1">
                                  {street.houses.map((house) => (
                                    <div key={house.houseId} className="flex items-center justify-between text-[10px] py-0.5 px-1.5 rounded bg-background border">
                                      <span className="font-medium">Nr. {house.houseNumber}</span>
                                      <span className="text-muted-foreground">
                                        {house.unitCount > 1 && `(${house.unitCount})`}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </CollapsibleContent>
                            </div>
                          </Collapsible>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
              );
            })}
            {details.length === 0 && (
              <p className="text-xs text-muted-foreground">Keine Daten für diesen Tag</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const TagBadge = ({ label, count, color }: { label: string; count: number; color: string }) => (
    count > 0 ? (
      <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${color}`}>
        <span>{label}</span>
        <span className="font-bold">{count}</span>
      </div>
    ) : null
  );

  const SmallTagBadge = ({ label, count, color, userId, isActive, onClick }: { 
    label: string; 
    count: number; 
    color: string; 
    userId?: string;
    isActive?: boolean;
    onClick?: () => void;
  }) => (
    count > 0 ? (
      <span 
        className={`px-1 py-0.5 rounded text-[10px] font-medium cursor-pointer transition-all ${color} ${
          isActive ? 'ring-2 ring-primary ring-offset-1' : 'hover:opacity-80'
        }`}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
      >
        {label}:{count}
      </span>
    ) : null
  );

  const TagBadges = ({ tags, userId }: { tags: DailyTagCount; userId: string }) => {
    const currentFilter = activeTagFilter[userId];
    
    const handleTagClick = (tag: string) => {
      setActiveTagFilter(prev => ({
        ...prev,
        [userId]: prev[userId] === tag ? null : tag
      }));
    };
    
    return (
      <div className="flex flex-wrap gap-1">
        <SmallTagBadge label="WIN" count={tags.win} color="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" isActive={currentFilter === 'WIN'} onClick={() => handleTagClick('WIN')} />
        <SmallTagBadge label="HOT" count={tags.hot} color="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" isActive={currentFilter === 'HOT'} onClick={() => handleTagClick('HOT')} />
        <SmallTagBadge label="KT" count={tags.kt} color="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" isActive={currentFilter === 'KT'} onClick={() => handleTagClick('KT')} />
        <SmallTagBadge label="BK" count={tags.bk} color="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" isActive={currentFilter === 'BK'} onClick={() => handleTagClick('BK')} />
        <SmallTagBadge label="NA" count={tags.na} color="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" isActive={currentFilter === 'NA'} onClick={() => handleTagClick('NA')} />
        <SmallTagBadge label="KI" count={tags.ki} color="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" isActive={currentFilter === 'KI'} onClick={() => handleTagClick('KI')} />
        <SmallTagBadge label="NO" count={tags.no} color="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400" isActive={currentFilter === 'NO'} onClick={() => handleTagClick('NO')} />
      </div>
    );
  };

  const renderDayDetails = (userId: string, day: DailyActivity) => {
    const dayKey = `${userId}-${day.date}`;
    const isDayExpanded = expandedDay === dayKey;
    const isLoadingThisDay = loadingDay === dayKey;
    const details = dayDetails[dayKey];

    return (
      <div key={day.date}>
        <div 
          className="flex items-center justify-between py-2 px-2 rounded-md hover:bg-muted/50 cursor-pointer"
          onClick={() => fetchDayDetails(userId, day.date)}
        >
          <div className="flex items-center gap-2">
            {isLoadingThisDay ? (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            ) : isDayExpanded ? (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )}
            <span className="text-sm">
              {format(new Date(day.date), 'EEEE, dd.MM.yyyy', { locale: de })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <TagBadges tags={day.tags} userId={userId} />
            <span className="text-sm font-medium text-primary min-w-[80px] text-right">
              {day.count} {day.count === 1 ? 'Einheit' : 'Einheiten'}
            </span>
          </div>
        </div>
        
        {isDayExpanded && details && (
          <div className="ml-6 pl-4 border-l-2 border-muted space-y-2 py-2">
            {details.map((area) => (
              <div key={area.areaId} className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <MapPin className="h-3 w-3 text-primary" />
                  {area.areaName}
                </div>
                <div className="ml-5 space-y-2">
                  {area.streets.map((street) => (
                    <div key={street.streetId} className="space-y-1">
                      <div className="flex items-center justify-between text-xs py-1 px-2 rounded bg-muted/30 font-medium">
                        <span>{street.streetName}</span>
                        <div className="flex items-center gap-1">
                          <TagBadges tags={street.tags} userId={userId} />
                          <span className="text-muted-foreground ml-1">({street.count})</span>
                        </div>
                      </div>
                      <div className="ml-4 grid grid-cols-2 sm:grid-cols-3 gap-1">
                        {street.houses.map((house) => (
                          <div key={house.houseId} className="flex items-center justify-between text-[10px] py-0.5 px-1.5 rounded bg-background border">
                            <span className="font-medium">Nr. {house.houseNumber}</span>
                            <div className="flex items-center gap-0.5">
                              {house.tags.win > 0 && <span className="text-green-600 font-bold">W</span>}
                              {house.tags.hot > 0 && <span className="text-orange-600 font-bold">H</span>}
                              {house.tags.kt > 0 && <span className="text-blue-600 font-bold">K</span>}
                              {house.tags.bk > 0 && <span className="text-purple-600 font-bold">B</span>}
                              {house.tags.na > 0 && <span className="text-yellow-600 font-bold">N</span>}
                              {house.tags.ki > 0 && <span className="text-red-600 font-bold">X</span>}
                              {house.tags.no > 0 && <span className="text-gray-600 font-bold">O</span>}
                              {house.unitCount > 1 && <span className="text-muted-foreground">({house.unitCount})</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {details.length === 0 && (
              <p className="text-xs text-muted-foreground">Keine Gebietsdetails verfügbar</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderHierarchicalActivity = (member: MemberStats) => {
    const tagFilter = activeTagFilter[member.userId];
    const filteredDailyActivity = filterDailyActivityByTag(member.dailyActivity, tagFilter);
    const filteredMonthlyGroups = filterMonthGroupsByTag(member.monthlyGroups, tagFilter);

    // Active filter indicator
    const FilterIndicator = () => tagFilter ? (
      <div className="flex items-center gap-2 mb-2 p-2 bg-primary/10 rounded-md">
        <span className="text-sm text-muted-foreground">Filter aktiv:</span>
        <span className="px-2 py-0.5 rounded text-xs font-bold bg-primary text-primary-foreground">{tagFilter}</span>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            setActiveTagFilter(prev => ({ ...prev, [member.userId]: null }));
          }}
          className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          <span>Filter löschen</span>
        </button>
      </div>
    ) : null;

    // For day/week view, show flat list
    if (timeRange === 'day' || timeRange === 'week') {
      return (
        <div className="space-y-1">
          <FilterIndicator />
          {filteredDailyActivity.slice(0, 14).map((day) => renderDayDetails(member.userId, day))}
          {filteredDailyActivity.length === 0 && tagFilter && (
            <p className="text-sm text-muted-foreground text-center py-4">Keine Einheiten mit Tag "{tagFilter}" gefunden</p>
          )}
        </div>
      );
    }

    // For month/year view, show hierarchical structure
    return (
      <div className="space-y-2">
        <FilterIndicator />
        {filteredMonthlyGroups.map((monthGroup) => {
          const monthKey = `${member.userId}-month-${monthGroup.month}`;
          const isMonthExpanded = expandedMonth === monthKey;

          return (
            <div key={monthKey} className="border rounded-lg overflow-hidden">
              <div 
                className="flex items-center justify-between p-3 bg-muted/30 cursor-pointer hover:bg-muted/50"
                onClick={() => setExpandedMonth(isMonthExpanded ? null : monthKey)}
              >
                <div className="flex items-center gap-2">
                  {isMonthExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="font-medium">{monthGroup.monthName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <TagBadges tags={monthGroup.tags} userId={member.userId} />
                  <span className="text-sm font-medium text-primary">
                    {monthGroup.totalCount} Einheiten
                  </span>
                </div>
              </div>

              {isMonthExpanded && (
                <div className="p-2 space-y-2">
                  {monthGroup.weeks.map((weekGroup) => {
                    const weekKey = `${member.userId}-week-${monthGroup.month}-${weekGroup.weekNumber}`;
                    const isWeekExpanded = expandedWeek === weekKey;
                    const weekLabel = `KW ${weekGroup.weekNumber} (${format(weekGroup.weekStart, 'dd.MM.', { locale: de })} - ${format(weekGroup.weekEnd, 'dd.MM.', { locale: de })})`;

                    return (
                      <div key={weekKey} className="border rounded-md overflow-hidden">
                        <div 
                          className="flex items-center justify-between p-2 bg-background cursor-pointer hover:bg-muted/30"
                          onClick={() => setExpandedWeek(isWeekExpanded ? null : weekKey)}
                        >
                          <div className="flex items-center gap-2">
                            {isWeekExpanded ? (
                              <ChevronDown className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-3 w-3 text-muted-foreground" />
                            )}
                            <span className="text-sm">{weekLabel}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <TagBadges tags={weekGroup.tags} userId={member.userId} />
                            <span className="text-sm font-medium text-primary">
                              {weekGroup.totalCount} Einheiten
                            </span>
                          </div>
                        </div>

                        {isWeekExpanded && (
                          <div className="p-2 pl-6 space-y-1 bg-muted/10">
                            {weekGroup.days.map((day) => renderDayDetails(member.userId, day))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {filteredMonthlyGroups.length === 0 && tagFilter && (
          <p className="text-sm text-muted-foreground text-center py-4">Keine Einheiten mit Tag "{tagFilter}" gefunden</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{getTimeRangeLabel()}</span>
        </div>
        <Select value={timeRange} onValueChange={(v) => setTimeRange(v as TimeRange)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Heute</SelectItem>
            <SelectItem value="week">Letzte 7 Tage</SelectItem>
            <SelectItem value="month">Dieser Monat</SelectItem>
            <SelectItem value="year">Dieses Jahr</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : memberStats.length === 0 ? (
        <div className="text-center py-12">
          <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Keine Daten vorhanden</h3>
          <p className="text-muted-foreground">
            Für den gewählten Zeitraum liegen keine Statistiken vor.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {memberStats.map((member) => {
            const isExpanded = expandedMember === member.userId;
            
            return (
              <Collapsible 
                key={member.userId} 
                open={isExpanded} 
                onOpenChange={() => {
                  setExpandedMember(isExpanded ? null : member.userId);
                  // Reset nested expansions when closing
                  if (isExpanded) {
                    setExpandedMonth(null);
                    setExpandedWeek(null);
                    setExpandedDay(null);
                  }
                }}
              >
                <div className="rounded-lg border bg-card">
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-accent/50 transition-colors">
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium">
                            {member.firstName} {member.lastName}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {member.totalUnits} Einheiten bearbeitet
                          </p>
                          <p className="text-xs text-muted-foreground/70">
                            Letzte Aktivität: {formatLastActivity(member.lastActivityAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.winCount > 0 && (
                          <div className="flex items-center gap-1 text-green-600">
                            <Trophy className="h-4 w-4" />
                            <span className="font-bold">{member.winCount}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="px-4 pb-4 pt-0 border-t">
                      <div className="pt-4 space-y-4">
                        {/* Tag Statistics */}
                        <div>
                          <h5 className="text-sm font-medium mb-2 flex items-center gap-2">
                            <Home className="h-4 w-4" />
                            Tag-Übersicht
                          </h5>
                          <div className="flex flex-wrap gap-2">
                            <ClickableTagBadge userId={member.userId} label="WIN" count={member.winCount} color="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" />
                            <ClickableTagBadge userId={member.userId} label="HOT" count={member.hotCount} color="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" />
                            <ClickableTagBadge userId={member.userId} label="KT" count={member.ktCount} color="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" />
                            <ClickableTagBadge userId={member.userId} label="BK" count={member.bkCount} color="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" />
                            <ClickableTagBadge userId={member.userId} label="NA" count={member.naCount} color="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" />
                            <ClickableTagBadge userId={member.userId} label="KI" count={member.kiCount} color="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" />
                            <ClickableTagBadge userId={member.userId} label="NO" count={member.noCount} color="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400" />
                          </div>
                        </div>

                        {/* Hierarchical Activity */}
                        {(member.dailyActivity.length > 0 || member.monthlyGroups.length > 0) && (
                          <div>
                            <h5 className="text-sm font-medium mb-2 flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              Aktivitäten
                            </h5>
                            {renderHierarchicalActivity(member)}
                          </div>
                        )}
                      </div>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>
      )}
    </div>
  );
}
